/**
 * Header notifications popover.
 *
 * Today's only notification source is the "case shared with me" event,
 * surfaced from the recent-cases payload (every case carrying a
 * `sharedBy` field counts as one notification). The unread badge is
 * computed against `user.uiPreferences.notificationsLastReadAt` —
 * opening the popover marks the current snapshot as read.
 *
 * The component is built to grow: when other notification kinds land
 * (invitation accepted, share role changed, comments on your case…),
 * extend the `Notification` union below and merge them in the same
 * list — the rendering layer doesn't care about the source.
 */
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  VStack,
  chakra,
  useDisclosure,
} from '@chakra-ui/react'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useEffect, useMemo, type FC } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'
import { useNavigate } from 'react-router-dom'
import { IoNotifications as RawNotifIcon } from 'react-icons/io5'
import { LuArrowRight, LuShare2 } from 'react-icons/lu'
import axiosClient from '../../api/axios'
import {
  useUiPreferences,
  useUpdateUiPreferences,
} from '../../api/api_hooks/user/uiPreferences'
import { useTranslation } from 'react-i18next'

const NotifIcon = chakra(RawNotifIcon as FC)

interface SharedNotification {
  kind: 'shared'
  caseId: string
  caseLabel: string
  pathologyName: string | null
  sharedAt: string
  sharedBy: { contactName: string | null; email: string } | null
}
type Notification = SharedNotification

export default function NotificationsPanel() {
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const { data: prefs } = useUiPreferences()
  const { mutateAsync: updatePrefs } = useUpdateUiPreferences()
  const lastReadAt = (prefs?.notificationsLastReadAt as string | undefined) || null

  // Reuse the same query key + URL as InProgressCases so we don't double-fetch.
  // scope=personal keeps this to own + shared-with-me (an admin would otherwise
  // get notifications for the whole fleet's cases).
  const { data } = useQuery<{ analyses: any[] }>({
    queryKey: ['blotRecentCases', 'home'],
    queryFn: async () => {
      const response = await axiosClient.get('/blot-analysis/recent-cases?limit=50&scope=personal')
      return response.data
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

  const notifications: Notification[] = useMemo(() => {
    const cases = data?.analyses ?? []
    return cases
      .filter((c) => c.sharedBy)
      .map((c) => ({
        kind: 'shared' as const,
        caseId: c.id,
        caseLabel: c.sampleId || c.filename || t('home.notifications.blotCase'),
        pathologyName: c.pathologyName ?? null,
        sharedAt: c.sharedAt || c.updatedAt,
        sharedBy: c.sharedBy
          ? { contactName: c.sharedBy.contactName, email: c.sharedBy.email }
          : null,
      }))
      .sort((a, b) => new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime())
  }, [data])

  const unreadCount = useMemo(() => {
    if (!notifications.length) return 0
    if (!lastReadAt) return notifications.length
    const cutoff = new Date(lastReadAt).getTime()
    return notifications.filter((n) => new Date(n.sharedAt).getTime() > cutoff).length
  }, [notifications, lastReadAt])

  // When the popover is opened, persist the current timestamp as
  // "everything seen up to here" — best-effort, no-op if offline.
  useEffect(() => {
    if (!isOpen) return
    if (unreadCount === 0) return
    updatePrefs({ notificationsLastReadAt: new Date().toISOString() }).catch(() => {})
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const goToCase = (caseId: string) => {
    onClose()
    navigate(`/lab/blot-analysis/${caseId}`)
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      closeOnBlur
    >
      <Tooltip label="Notifications" hasArrow placement="bottom" openDelay={250} bg="gray.900" color="white" borderRadius="md">
        <Box position="relative" display="inline-flex">
          <PopoverTrigger>
            <IconButton
              aria-label="Notifications"
              icon={<NotifIcon boxSize={5} />}
              variant="ghost"
              color="white"
              borderRadius="xl"
              size="md"
              bg="rgba(255,255,255,0.08)"
              border="1px solid"
              borderColor="whiteAlpha.200"
              _hover={{ bg: 'rgba(255,255,255,0.18)', borderColor: 'whiteAlpha.400', transform: 'translateY(-2px)' }}
              transition="all 0.22s cubic-bezier(0.4,0,0.2,1)"
            />
          </PopoverTrigger>
          {unreadCount > 0 && (
            <Box
              position="absolute"
              top="-2px"
              right="-2px"
              minW="18px"
              h="18px"
              px="4px"
              borderRadius="full"
              bg="#ef4444"
              color="white"
              fontSize="10px"
              fontWeight="800"
              display="grid"
              sx={{ placeItems: 'center' }}
              boxShadow="0 0 0 2px rgba(76, 29, 149, 0.55)"
              pointerEvents="none"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Box>
          )}
        </Box>
      </Tooltip>

      <Portal>
      <PopoverContent
        w={{ base: '340px', md: '400px' }}
        borderRadius="2xl"
        boxShadow="0 24px 64px -12px rgba(15, 23, 42, 0.45)"
        border="1px solid"
        borderColor="gray.100"
        overflow="hidden"
        zIndex={1500}
        _focus={{ outline: 'none' }}
      >
        <PopoverArrow />
        <PopoverHeader
          bgGradient={CHROME_HEADER_GRADIENT}
          color="white"
          fontWeight="700"
          fontSize="sm"
          py={3}
          px={4}
          border="none"
        >
          <HStack justify="space-between">
            <Text>Notifications</Text>
            {notifications.length > 0 && (
              <Text fontSize="xs" fontWeight="500" color="whiteAlpha.800">
                {notifications.length} récente{notifications.length > 1 ? 's' : ''}
              </Text>
            )}
          </HStack>
        </PopoverHeader>
        <PopoverCloseButton color="white" top={2.5} right={3} />
        <PopoverBody p={0} maxH="420px" overflowY="auto">
          {notifications.length === 0 ? (
            <VStack spacing={2} py={8} px={4}>
              <Icon as={NotifIcon} boxSize={6} color="gray.300" />
              <Text fontSize="sm" color="gray.500" fontWeight="600">
                Aucune notification
              </Text>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                Quand un collègue partage un cas avec vous, l'alerte apparaît ici.
              </Text>
            </VStack>
          ) : (
            <VStack spacing={0} align="stretch" divider={<Box borderTop="1px solid" borderColor="gray.100" />}>
              {notifications.map((n) => {
                const isUnread = !lastReadAt || new Date(n.sharedAt).getTime() > new Date(lastReadAt).getTime()
                const sharerName = n.sharedBy?.contactName || n.sharedBy?.email.split('@')[0] || t('home.notifications.aColleague')
                return (
                  <Box
                    key={`${n.kind}-${n.caseId}`}
                    px={4}
                    py={3}
                    cursor="pointer"
                    transition="background 0.15s"
                    bg={isUnread ? 'teal.50' : 'white'}
                    _hover={{ bg: isUnread ? 'teal.100' : 'gray.50' }}
                    onClick={() => goToCase(n.caseId)}
                    role="group"
                  >
                    <HStack spacing={3} align="start">
                      <Box
                        w="32px" h="32px" minW="32px"
                        borderRadius="lg"
                        bgGradient="linear(135deg, teal.500, cyan.500)"
                        color="white"
                        display="grid"
                        sx={{ placeItems: 'center' }}
                      >
                        <Icon as={LuShare2} boxSize={4} />
                      </Box>
                      <Box flex={1} minW={0}>
                        <HStack spacing={2} mb={0.5}>
                          <Text fontSize="sm" fontWeight={isUnread ? '700' : '600'} color="gray.900" noOfLines={1}>
                            {sharerName} vous a partagé un cas
                          </Text>
                          {isUnread && <Box w="7px" h="7px" borderRadius="full" bg="#ef4444" flexShrink={0} />}
                        </HStack>
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>
                          {n.caseLabel}
                          {n.pathologyName ? ` · ${n.pathologyName}` : ''}
                        </Text>
                        <HStack justify="space-between" mt={1.5}>
                          <Text fontSize="2xs" color="gray.400">
                            {formatDistanceToNow(new Date(n.sharedAt), { addSuffix: true, locale: fr })}
                          </Text>
                          <HStack spacing={1} fontSize="2xs" fontWeight="600" color="teal.600" _groupHover={{ gap: 2 }}>
                            <Text>Ouvrir</Text>
                            <Icon as={LuArrowRight} boxSize={3} />
                          </HStack>
                        </HStack>
                      </Box>
                    </HStack>
                  </Box>
                )
              })}
            </VStack>
          )}
        </PopoverBody>
        {notifications.length > 0 && (
          <Box borderTop="1px solid" borderColor="gray.100" p={2} bg="gray.50">
            <Button
              variant="ghost"
              size="sm"
              w="100%"
              colorScheme="purple"
              rightIcon={<LuArrowRight />}
              onClick={() => { onClose(); navigate('/lab/blot-analysis') }}
            >
              Tout voir
            </Button>
          </Box>
        )}
      </PopoverContent>
      </Portal>
    </Popover>
  )
}
